import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { business, pages } from "../data/business";

const ORIGIN = "https://ocoeeriverexperience.com";
const DEFAULT_TITLE = `${business.name} · Rafting, Lodging & Dining · TN`;
const DEFAULT_DESCRIPTION =
  "Guided whitewater rafting on the Middle Ocoee, plus on-site lodging, camping, and dining near the Ocoee River in Benton, TN.";

const staticMeta: Record<string, { title: string; description: string }> = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/contact": {
    title: `Contact — ${business.name}`,
    description:
      "Call, email, or get directions to Ocoee River Experience at 414 Parksville Road in Benton, TN.",
  },
  "/faq": {
    title: `FAQ — ${business.name}`,
    description:
      "Answers about Ocoee rafting experience, rain policy, what to wear, minimum age, and non-rafting guests.",
  },
  "/what-to-bring": {
    title: `What to Bring — ${business.name}`,
    description:
      "Packing list for an Ocoee rafting day: shoes, swimsuit, dry clothes, towel, sunscreen, and what to leave behind.",
  },
  "/safety": {
    title: `Safety on the Water — ${business.name}`,
    description:
      "How Ocoee River Experience manages whitewater rafting risk with equipment, guides, briefings, and waivers.",
  },
  "/groups": {
    title: `Groups & Corporate Trips — ${business.name}`,
    description:
      "Group rafting, lodging, dining, and event planning for corporate trips, reunions, and teams on the Ocoee.",
  },
  "/weddings": {
    title: `Weddings — ${business.name}`,
    description:
      "Wedding and reception planning near the Ocoee River, with on-site lodging, dining, and rafting options for guests.",
  },
  "/scouts": {
    title: `Schools, Scouts & Youth Groups — ${business.name}`,
    description:
      "Youth group, school, and scout outing options with guided rafting, lodging, dining, and local ecology.",
  },
  "/waiver": {
    title: `Waiver & Liability Release — ${business.name}`,
    description:
      "Waiver and liability release information for guided rafting trips at Ocoee River Experience.",
  },
  "/cancellation": {
    title: `Cancellation Policy — ${business.name}`,
    description:
      "Cancellation, rebooking, no-show, weather, and dam-release policy for Ocoee River Experience bookings.",
  },
  "/privacy": {
    title: `Privacy Policy — ${business.name}`,
    description:
      "Privacy policy for guest contact details, booking data, communications, and data requests.",
  },
  "/terms": {
    title: `Terms of Service — ${business.name}`,
    description:
      "Terms for reservations, guest conduct, liability, and on-property participation.",
  },
  "/accessibility": {
    title: `Accessibility Statement — ${business.name}`,
    description:
      "Accessibility information for the Ocoee River Experience property and website.",
  },
};

// Item #71 — theme-color per route. Tints the mobile chrome (iOS status
// bar in PWA standalone, Chrome address bar) to match the dominant
// surface of each section. All values picked from the existing token
// palette — no new brand colours invented.
//
//   --surface-inverse    = #1B3A28  (default forest green)
//   --surface-darker     = #0F2A1A  (deeper green, used on BBQ + dining)
//   --surface-gold-tint  = #142F1F  (gold-shifted green, used on lodging)
const DEFAULT_THEME_COLOR = "#1B3A28";
const themeColorByPath: Record<string, string> = {
  // Smokehouse pages run the darker variant so the address bar reads as
  // the venue's pit-fire mood rather than the lobby green.
  "/bbq": "#0F2A1A",
  "/sugarloaf": "#0F2A1A",
  // Lodging gets the gold-tinted surface — already used by lodge cards.
  "/lodge": "#142F1F",
  "/bunkhouse": "#142F1F",
  "/camping": "#142F1F",
};

function getThemeColorForRoute(pathname: string): string {
  const normalized = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  return themeColorByPath[normalized] ?? DEFAULT_THEME_COLOR;
}

// Per-route robots directive. Default is to index everything; specific
// routes get scoped overrides (placeholder /shop "Coming soon" stays
// out of the index until real merchandise ships, etc.). The default
// matches what's in index.html — RouteHead keeps it in sync on every
// navigation so the meta tag never drifts.
const DEFAULT_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const robotsByPath: Record<string, string> = {
  // /shop is currently a "Coming soon" placeholder — keep out of the
  // index until there's real product content to surface.
  "/shop": "noindex, follow",
};
function getRobotsForRoute(pathname: string): string {
  const normalized = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  return robotsByPath[normalized] ?? DEFAULT_ROBOTS;
}

// Build a BreadcrumbList JSON-LD payload for the current route so each
// page gets schema.org breadcrumbs in Google's SERP. Crawlers also use
// this signal to model the site's IA. We anchor everything at the
// origin so the JSON references the canonical URL even on Vercel
// preview deploys.
function getBreadcrumbJsonLd(pathname: string, title: string) {
  const normalized = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const items: Array<{ name: string; url: string }> = [
    { name: "Home", url: `${ORIGIN}/` },
  ];
  if (normalized !== "/") {
    items.push({ name: title, url: `${ORIGIN}${normalized}` });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

function getRouteMeta(pathname: string) {
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const staticEntry = staticMeta[normalizedPath];
  if (staticEntry) return staticEntry;

  const slug = normalizedPath.slice(1);
  const page = pages[slug];
  if (page) {
    return {
      title: `${page.title} — ${business.name}`,
      description: page.subtitle,
    };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
}

// Single component that keeps <link rel="canonical"> + og:url in sync with
// the current route. Avoids per-page boilerplate and prevents duplicate-
// content SEO penalties.
export function RouteHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const url = `${ORIGIN}${pathname}`;
    const meta = getRouteMeta(pathname);

    const setOrCreate = (
      selector: string,
      attrs: Record<string, string>,
    ) => {
      let el = document.head.querySelector(selector) as HTMLElement | null;
      if (!el) {
        const tag = selector.startsWith("link") ? "link" : "meta";
        el = document.createElement(tag);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    setOrCreate('link[rel="canonical"]', { rel: "canonical", href: url });
    setOrCreate('meta[name="description"]', { name: "description", content: meta.description });
    setOrCreate('meta[property="og:title"]', { property: "og:title", content: meta.title });
    setOrCreate('meta[property="og:description"]', { property: "og:description", content: meta.description });
    setOrCreate('meta[property="og:url"]', { property: "og:url", content: url });
    setOrCreate('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    setOrCreate('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });

    // Item #71 — keep theme-color in sync with the current route.
    setOrCreate('meta[name="theme-color"]', {
      name: "theme-color",
      content: getThemeColorForRoute(pathname),
    });

    // SEO/indexing — per-route robots directive. Defaults to "index,
    // follow + rich snippets" but /shop placeholder gets "noindex".
    setOrCreate('meta[name="robots"]', {
      name: "robots",
      content: getRobotsForRoute(pathname),
    });

    // BreadcrumbList JSON-LD — managed inline so it updates per
    // navigation. Reuses a stable id so we don't accumulate script
    // tags across route changes.
    let crumbScript = document.head.querySelector<HTMLScriptElement>(
      "script#breadcrumbs-jsonld",
    );
    if (!crumbScript) {
      crumbScript = document.createElement("script");
      crumbScript.id = "breadcrumbs-jsonld";
      crumbScript.type = "application/ld+json";
      document.head.appendChild(crumbScript);
    }
    crumbScript.textContent = JSON.stringify(
      getBreadcrumbJsonLd(pathname, meta.title.split(" — ")[0] ?? meta.title),
    );
  }, [pathname]);

  return null;
}
